import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  adminApproveVersion,
  adminCreateIngredient,
  adminDiscardDraft,
  adminGetVersion,
  adminListIngredients,
  adminListRecipeStandards,
  adminSetServiceStatus,
  adminSubmitForReview,
  adminUpdateDraft,
  type AdminEditorFinishStepRow,
  type AdminEditorIngredientRow,
  type AdminIngredientMaster,
  type RecipeIngredientOption,
  type RecipeStandardKind,
  type RecipeStandardRow,
  type DrinkServiceStatus,
  type UpdateDraftPayload,
} from "../../lib/api";
import RecipeIngredientPicker from "../../components/admin/RecipeIngredientPicker";
import type { SessionUser } from "../../types/auth";
import type { FinishScaleType, FinishType, ScaleType } from "../../types/drink";
import "./AdminRecipeEditor.css";
import Field from "../../components/admin/Field";
import MenuToggle from "../../components/admin/MenuToggle";
import { useConfirmDialog } from "../../components/ConfirmDialog";

type EditorRow = {
  // Stable ID for React key — synthesized from server `id` or "tmp-" + nonce
  key: string;
  ingredient_id: string;
  selected: RecipeIngredientOption | null;
  display_name: string;
  amount: string; // string for editing — converted on save
  unit: string;
  scale_type: ScaleType;
};

type FinishStepRow = {
  key: string;
  finish_type: FinishType;
  label: string;
  instruction: string;
  quantity: string; // string for editing
  unit: string;
  scale_type: FinishScaleType;
  required: boolean;
};

const FINISH_TYPES: FinishType[] = [
  "Cream Top",
  "Expressed Citrus",
  "Float",
  "Foam",
  "Garnish",
  "Mist",
  "Other",
  "Presentation",
  "Rim",
  "Serveware",
  "Smoke",
  "Topper",
];

const FINISH_SCALE_TYPES: { value: FinishScaleType; label: string }[] = [
  { value: "per_cocktail", label: "Per cocktail" },
  { value: "do_not_scale", label: "Don't scale" },
  { value: "optional", label: "Optional" },
  { value: "batch_finish", label: "Batch finish" },
];

const SCALE_TYPES: { value: ScaleType; label: string }[] = [
  { value: "exact", label: "Exact" },
  { value: "do_not_scale", label: "Don't scale" },
  { value: "per_cocktail", label: "Per cocktail" },
  { value: "approximate", label: "Approximate" },
  { value: "garnish_each", label: "Garnish each" },
  { value: "batch_only", label: "Batch only (hidden)" },
];

const SERVICE_STATUSES = ["Active", "Seasonal", "Event Only", "Hidden", "86d", "Retired"];
// Mirrors REQUIRES_APPROVED in bar-admin-set-service-status, which refuses these (409) for a
// drink with no approved version; the dropdown greys them out so the refusal isn't a surprise.
const REQUIRES_APPROVED = new Set(["Active", "Seasonal", "Event Only", "86d"]);
const RECIPE_STATUSES = ["Draft", "Testing", "Needs Review"];

// Must match the live bar_manual.ingredients_category_check — anything else fails the insert.
const INGREDIENT_CATEGORIES = [
  "spirit",
  "liqueur",
  "bitters",
  "syrup",
  "juice",
  "garnish",
  "prep_item",
  "dairy",
  "egg",
  "wine",
  "vermouth",
  "other",
];

const INGREDIENT_UNITS = [
  "oz",
  "ml",
  "dash",
  "drops",
  "barspoon",
  "splash",
  "each",
  "leaf",
  "sprig",
  "wedge",
];

let tmpCounter = 1;

export default function AdminRecipeEditor() {
  const navigate = useNavigate();
  const { versionId } = useParams<{ versionId: string }>();
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = user.bar_roles.includes("bar_owner");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  // Dirty tracking — set by any user edit, cleared on save/discard.
  // Used to (a) warn beforeunload and (b) gate the "← Back" button so
  // form data isn't silently lost on navigation (prep editor has full
  // autosave; this larger editor has manual save only, so we guard the exit).
  const dirtyRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  // Tracked + cleared on unmount/retrigger so a stale setTimeout doesn't
  // clear a newer feedback flash early or fire after unmount.
  const feedbackTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
  }, []);
  function flashFeedback(msg: string, ms = 2400) {
    setFeedback(msg);
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = window.setTimeout(() => setFeedback(null), ms);
  }
  const markDirty = () => {
    if (initialLoadDoneRef.current) dirtyRef.current = true;
  };

  const [drinkId, setDrinkId] = useState<string | null>(null);
  const [drinkSlug, setDrinkSlug] = useState<string | null>(null);
  const [versionNumber, setVersionNumber] = useState<string>("—");
  const [versionStatus, setVersionStatus] = useState<string>("Draft");
  const [readOnly, setReadOnly] = useState(false);

  const featuredUntilRef = useRef<string | null>(null);

  // Section A
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [aliasesRaw, setAliasesRaw] = useState("");

  // Section B
  const [recipeStatus, setRecipeStatus] = useState<"Draft" | "Testing" | "Needs Review">("Draft");
  // The drink's live service_status. It is NOT part of the draft: the toggle and the dropdown
  // both save it immediately through bar-admin-set-service-status (the same call as the
  // Cocktail Specs list, which enforces the approved-version rule), and buildPayload leaves it
  // out, so a draft save can never overwrite it or race it.
  const [serviceStatus, setServiceStatus] = useState("Hidden");
  const [hasApprovedVersion, setHasApprovedVersion] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [featuredToday, setFeaturedToday] = useState(false);

  // Section C
  const [glass, setGlass] = useState("");
  const [ice, setIce] = useState("");
  const [method, setMethod] = useState("");
  const [garnish, setGarnish] = useState("");
  const [vessel, setVessel] = useState("");
  const [smoked, setSmoked] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [warning, setWarning] = useState("");

  // Section D
  const [ingredientMaster, setIngredientMaster] = useState<AdminIngredientMaster[]>([]);
  const [rows, setRows] = useState<EditorRow[]>([]);

  // Section E
  const [instructions, setInstructions] = useState<{ key: string; text: string }[]>([]);

  // Section F (Phase 8b — finish / presentation steps)
  const [finishSteps, setFinishSteps] = useState<FinishStepRow[]>([]);

  // Section H (was G — re-lettered when Section F became finish steps)
  const [staffNotes, setStaffNotes] = useState("");
  const [internalAdminNotes, setInternalAdminNotes] = useState("");
  const [testingNotes, setTestingNotes] = useState("");
  const [changeSummary, setChangeSummary] = useState("");

  // Recipe standards (admin-managed dropdowns for glass/ice/method)
  const [standards, setStandards] = useState<{
    glass: RecipeStandardRow[];
    ice: RecipeStandardRow[];
    method: RecipeStandardRow[];
  } | null>(null);

  // ── Load data ──
  useEffect(() => {
    if (!versionId) return;
    setLoading(true);
    setError(null);
    void adminListIngredients()
      .then((res) => setIngredientMaster(res.ingredients))
      .catch((err) => setError(err?.message ?? "Could not load custom ingredients"));
    void adminListRecipeStandards()
      .then((r) => setStandards(r.standards))
      .catch(() => undefined);
    void adminGetVersion(versionId)
      .then((data) => {
        const v = data.version;
        const d = data.drink;
        setDrinkId(d.id);
        setDrinkSlug(d.slug);
        setVersionNumber(v.version_number);
        setVersionStatus(v.status);
        setReadOnly(v.status === "Approved" || v.status === "Retired");

        setName(d.name);
        setCategory(d.category);
        setTagsRaw((d.tags ?? []).join(", "));
        setAliasesRaw((data.aliases ?? []).join(", "));

        setRecipeStatus(
          (RECIPE_STATUSES.includes(v.status) ? v.status : "Draft") as
            | "Draft"
            | "Testing"
            | "Needs Review"
        );
        setServiceStatus(d.service_status);
        setHasApprovedVersion(!!d.active_approved_version_id);
        featuredUntilRef.current = d.manual_featured_today_until &&
          new Date(d.manual_featured_today_until).getTime() > Date.now()
          ? d.manual_featured_today_until : null;
        setFeaturedToday(!!featuredUntilRef.current);

        setGlass(v.glass ?? "");
        setIce(v.ice ?? "");
        setMethod(v.method ?? "");
        setGarnish(v.garnish ?? "");
        setVessel(v.vessel ?? "");
        setSmoked(v.smoked ?? false);
        setFrozen(v.frozen ?? false);
        setWarning(v.warning ?? "");

        setRows(
          data.ingredients.map((row: AdminEditorIngredientRow) => ({
            key: `srv-${row.id}`,
            ingredient_id: row.catalog_item_id || row.menu_item_id || row.ingredient_id || row.ingredient?.id || "",
            selected: {
              id: row.catalog_item_id || row.menu_item_id || row.ingredient_id || row.ingredient?.id || "",
              source: row.catalog_item_id ? "catalog_item" : row.menu_item_id ? "menu_item" : "ingredient",
              name: row.ingredient?.name || row.display_name || "Unavailable ingredient",
              category: row.ingredient?.category || "",
              default_unit: row.ingredient?.default_unit || row.unit,
              available: row.ingredient?.available ?? false,
              is_untracked: row.ingredient?.is_untracked ?? false,
            },
            display_name: row.display_name ?? "",
            amount: String(row.amount),
            unit: row.unit,
            scale_type: row.scale_type,
          }))
        );

        setInstructions((v.instructions ?? []).map((text: string) => ({ key: `step-${tmpCounter++}`, text })));

        setFinishSteps(
          (data.finish_steps ?? []).map((row: AdminEditorFinishStepRow) => ({
            key: `srv-${row.id}`,
            finish_type: row.finish_type,
            label: row.label,
            instruction: row.instruction ?? "",
            quantity: row.quantity != null ? String(row.quantity) : "",
            unit: row.unit ?? "",
            scale_type: row.scale_type,
            required: row.required,
          })),
        );

        setStaffNotes(v.staff_notes ?? "");
        setInternalAdminNotes(v.internal_admin_notes ?? "");
        setTestingNotes(v.testing_notes ?? "");
        setChangeSummary(v.change_summary ?? "");
      })
      .catch((err) => setError(err?.message ?? "Could not load version"))
      .finally(() => {
        setLoading(false);
        // Flag the initial load as done so future setState calls flag dirty.
        // Deferred a tick so the load's own setState batch doesn't trip it.
        setTimeout(() => { initialLoadDoneRef.current = true; }, 0);
      });
  }, [versionId]);

  // beforeunload guard — browsers ignore custom messages now, but
  // returning a string still shows the native "Leave site?" prompt.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Single watch effect for dirty tracking — any change to a tracked
  // field after initial load flips dirtyRef. Avoids sprinkling
  // markDirty() into ~30 onChange handlers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { markDirty(); }, [
    name, category, tagsRaw, aliasesRaw,
    recipeStatus, featuredToday,
    glass, ice, method, garnish, vessel, smoked, frozen, warning,
    rows, instructions, finishSteps,
    staffNotes, internalAdminNotes, testingNotes, changeSummary,
  ]);

  function buildPayload(): UpdateDraftPayload {
    if (!versionId) throw new Error("No version_id");
    return {
      version_id: versionId,
      drink: {
        name: name.trim(),
        category: category.trim(),
        tags: tagsRaw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        manual_featured_today_until: featuredToday
          ? featuredUntilRef.current ?? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
          : null,
      },
      version: {
        glass: glass.trim() || null,
        ice: ice.trim() || null,
        method: method.trim() || null,
        garnish: garnish.trim() || null,
        vessel: vessel.trim() || null,
        smoked,
        frozen,
        instructions: instructions.map((s) => s.text.trim()).filter(Boolean),
        staff_notes: staffNotes.trim() || null,
        internal_admin_notes: internalAdminNotes.trim() || null,
        testing_notes: testingNotes.trim() || null,
        warning: warning.trim() || null,
        change_summary: changeSummary.trim() || null,
        status: recipeStatus,
      },
      ingredients: rows
        .filter((r) => r.ingredient_id)
        .map((r, idx) => {
          return {
            sort_order: idx,
            ingredient_id: r.selected?.source === "ingredient" ? r.ingredient_id : null,
            menu_item_id: r.selected?.source === "menu_item" ? r.ingredient_id : null,
            catalog_item_id: r.selected?.source === "catalog_item" ? r.ingredient_id : null,
            display_name: r.display_name.trim() || r.selected?.name || null,
            amount: parseFloat(r.amount) || 0,
            unit: r.unit.trim() || "oz",
            scale_type: r.scale_type,
          };
        }),
      finish_steps: finishSteps
        .filter((f) => f.label.trim().length > 0)
        .map((f, idx) => ({
          sort_order: idx,
          finish_type: f.finish_type,
          label: f.label.trim(),
          instruction: f.instruction.trim() || null,
          quantity: f.quantity.trim() ? parseFloat(f.quantity) : null,
          unit: f.unit.trim() || null,
          prep_recipe_id: null,
          scale_type: f.scale_type,
          required: f.required,
        })),
      aliases: aliasesRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }

  /** Validate the in-memory form before save so we don't silently strip
   *  ingredient rows that have an amount but no ingredient_id, or coerce
   *  a typo'd "1..5" amount to 0. Returns null when ok, or a user-facing
   *  error string when the save should be blocked. */
  function validateBeforeSave(): string | null {
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      // If the row was even partially filled in, both fields are required.
      const hasIngredient = !!r.ingredient_id;
      const amountStr = r.amount.trim();
      const looksFilled = hasIngredient || amountStr.length > 0 || r.display_name.trim().length > 0;
      if (!looksFilled) continue;
      if (!hasIngredient) {
        return `Ingredient row ${i + 1} has no ingredient picked. Pick one or clear the row to save.`;
      }
      if (amountStr.length > 0) {
        const n = parseFloat(amountStr);
        if (!Number.isFinite(n) || n < 0) {
          return `Ingredient row ${i + 1} has an invalid amount "${r.amount}".`;
        }
      }
    }
    return null;
  }

  async function setLiveServiceStatus(next: string) {
    if (!drinkId || statusBusy || next === serviceStatus) return;
    setStatusBusy(true);
    setError(null);
    try {
      await adminSetServiceStatus(drinkId, next as DrinkServiceStatus);
      setServiceStatus(next);
      flashFeedback(
        next === "Active" ? "On menu — bartenders can see it now."
        : next === "Hidden" ? "Off menu — hidden from bartenders."
        : `Service status set to ${next}.`
      );
    } catch (err: any) {
      setError(err?.message ?? "Could not change service status");
    } finally {
      setStatusBusy(false);
    }
  }

  async function saveCurrentDraft(): Promise<boolean> {
    const payload = buildPayload();
    let result;
    try {
      result = await adminUpdateDraft(payload);
    } catch (error: any) {
      if (error?.saved !== true) throw error;
      dirtyRef.current = false;
      featuredUntilRef.current = payload.drink?.manual_featured_today_until ?? null;
      setFeedback(null);
      setError(error.message);
      return false;
    }
    dirtyRef.current = false;
    featuredUntilRef.current = payload.drink?.manual_featured_today_until ?? null;
    if (result.audit_recorded === false) {
      setFeedback(null);
      setError(result.warning || "Recipe saved, but change history was not recorded. Contact a manager before publishing.");
      return false;
    }
    return true;
  }

  async function onSave() {
    const validationErr = validateBeforeSave();
    if (validationErr) {
      setError(validationErr);
      return;
    }
    setSaving(true);
    setError(null);
    setFeedback(null);
    try {
      if (!await saveCurrentDraft()) return;
      dirtyRef.current = false;
      flashFeedback("Saved.");
    } catch (err: any) {
      setError(err?.message ?? "Could not save draft");
    } finally {
      setSaving(false);
    }
  }

  async function onSubmitForReview() {
    if (!versionId) return;
    const validationErr = validateBeforeSave();
    if (validationErr) {
      setError(validationErr);
      return;
    }
    if (!await confirm({
      title: "Submit draft for review?",
      message: "Status changes to 'Needs Review'. You can keep editing until it's approved.",
      confirmLabel: "Submit",
    })) return;
    setSubmittingReview(true);
    setError(null);
    try {
      // Save first, then transition status
      if (!await saveCurrentDraft()) return;
      await adminSubmitForReview(versionId);
      dirtyRef.current = false;
      setRecipeStatus("Needs Review");
      setVersionStatus("Needs Review");
      setFeedback("Submitted for review.");
    } catch (err: any) {
      setError(err?.message ?? "Could not submit for review");
    } finally {
      setSubmittingReview(false);
    }
  }

  /**
   * Owner-only one-tap save + approve. Skips the "Needs Review" step
   * since owners ARE the reviewer. Saves current edits first so the
   * approved version reflects the latest state, then calls
   * bar-admin-approve-version (change_summary is optional as of
   * 2026-05-01). Navigates back to the recipe view on success.
   */
  async function onSubmitAndPublish() {
    if (!versionId) return;
    if (!isOwner) return; // belt-and-suspenders; UI hides the button for non-owners
    const validationErr = validateBeforeSave();
    if (validationErr) {
      setError(validationErr);
      return;
    }
    if (!await confirm({
      title: "Save edits and publish as live version?",
      message: "This retires the previous Approved version and bartenders see the new recipe immediately.",
      confirmLabel: "Publish",
    })) return;
    setPublishing(true);
    setError(null);
    try {
      // Save first so the approved version reflects whatever's on screen.
      if (!await saveCurrentDraft()) return;
      // Keep the author's optional note on the publication event as well.
      await adminApproveVersion(versionId, changeSummary.trim());
      dirtyRef.current = false;
      setFeedback("Published.");
      // Bounce back to the recipe view so the new live version is visible.
      if (drinkSlug) {
        navigate(`/admin/recipes/${drinkSlug}`);
      } else {
        navigate("/admin/recipes");
      }
    } catch (err: any) {
      setError(err?.message ?? "Could not publish");
    } finally {
      setPublishing(false);
    }
  }

  async function onDiscard() {
    if (!versionId) return;
    if (!await confirm({
      title: "Discard this draft?",
      message: "Cannot be undone.",
      confirmLabel: "Discard",
      danger: true,
    })) return;
    setDiscarding(true);
    setError(null);
    try {
      const resp = await adminDiscardDraft(versionId);
      dirtyRef.current = false;
      navigate(
        resp.drink_also_deleted || !drinkSlug
          ? "/admin/recipes"
          : `/admin/recipes/${drinkSlug}`
      );
    } catch (err: any) {
      setError(err?.message ?? "Could not discard draft");
    } finally {
      setDiscarding(false);
    }
  }

  // ── Ingredient row helpers ──
  function addIngredientRow() {
    setRows((prev) => [
      ...prev,
      {
        key: `tmp-${tmpCounter++}`,
        ingredient_id: "",
        selected: null,
        display_name: "",
        amount: "1",
        unit: "oz",
        scale_type: "exact",
      },
    ]);
  }
  function updateRow(idx: number, patch: Partial<EditorRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeRow(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveRow(from: number, to: number) {
    if (to < 0 || to >= rows.length) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }
  function duplicateRow(idx: number) {
    setRows((prev) => {
      const next = [...prev];
      const src = next[idx];
      next.splice(idx + 1, 0, { ...src, key: `tmp-${tmpCounter++}` });
      return next;
    });
  }

  // ── Quick Add Ingredient state & handler ──
  const [showQuickIngModal, setShowQuickIngModal] = useState<{ targetRowIdx: number | null } | null>(null);
  const [quickIngName, setQuickIngName] = useState("");
  const [quickIngCategory, setQuickIngCategory] = useState("spirit");
  const [quickIngUnit, setQuickIngUnit] = useState("oz");
  const [quickIngUntracked, setQuickIngUntracked] = useState(false);
  const [quickIngSaving, setQuickIngSaving] = useState(false);
  const [quickIngError, setQuickIngError] = useState<string | null>(null);

  async function handleQuickCreateIngredient() {
    const trimmed = quickIngName.trim();
    if (!trimmed) {
      setQuickIngError("Ingredient / bottle name is required");
      return;
    }
    setQuickIngSaving(true);
    setQuickIngError(null);
    try {
      const res = await adminCreateIngredient({
        name: trimmed,
        category: quickIngCategory,
        default_unit: quickIngUnit,
        is_untracked: quickIngUntracked,
      });
      if (res.ok && res.ingredient) {
        const newIng: AdminIngredientMaster = {
          id: res.ingredient.id,
          name: res.ingredient.name,
          category: res.ingredient.category,
          default_unit: res.ingredient.default_unit,
          available: res.ingredient.available,
          is_untracked: res.ingredient.is_untracked,
          allergen_flags: res.ingredient.allergen_flags || [],
          notes: res.ingredient.notes,
        };
        setIngredientMaster((prev) => [...prev, newIng].sort((a, b) => a.name.localeCompare(b.name)));
        const isGarnish = newIng.category === "garnish";
        if (showQuickIngModal?.targetRowIdx !== null && showQuickIngModal?.targetRowIdx !== undefined) {
          updateRow(showQuickIngModal.targetRowIdx, {
            ingredient_id: newIng.id,
            selected: { ...newIng, source: "ingredient", is_untracked: !!newIng.is_untracked },
            display_name: "",
            unit: newIng.default_unit,
            ...(isGarnish ? { scale_type: "garnish_each" as ScaleType } : {}),
          });
        } else {
          setRows((prev) => [
            ...prev,
            {
              key: `tmp-${tmpCounter++}`,
              ingredient_id: newIng.id,
              selected: { ...newIng, source: "ingredient", is_untracked: !!newIng.is_untracked },
              display_name: "",
              amount: "1",
              unit: newIng.default_unit,
              scale_type: isGarnish ? "garnish_each" : "exact",
            },
          ]);
        }
        flashFeedback(`Added "${newIng.name}" to database & selected in recipe!`);
        setShowQuickIngModal(null);
        setQuickIngName("");
        setQuickIngCategory("spirit");
        setQuickIngUnit("oz");
        setQuickIngUntracked(false);
      }
    } catch (err: any) {
      setQuickIngError(err?.message || "Failed to create ingredient");
    } finally {
      setQuickIngSaving(false);
    }
  }

  async function handleQuickAddBasic(name: string, category: string, default_unit: string, default_amount = "1") {
    let existing = ingredientMaster.find((i) => i.name.toLowerCase() === name.toLowerCase());
    if (!existing) {
      try {
        const res = await adminCreateIngredient({
          name,
          category,
          default_unit,
          is_untracked: true,
        });
        if (res.ok && res.ingredient) {
          existing = {
            id: res.ingredient.id,
            name: res.ingredient.name,
            category: res.ingredient.category,
            default_unit: res.ingredient.default_unit,
            available: res.ingredient.available,
            is_untracked: true,
            allergen_flags: res.ingredient.allergen_flags || [],
            notes: res.ingredient.notes,
          };
          setIngredientMaster((prev) => [...prev, existing!].sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (err: any) {
        flashFeedback(`Could not add "${name}": ${err?.message}`);
        return;
      }
    }
    if (existing) {
      setRows((prev) => [
        ...prev,
        {
          key: `tmp-${tmpCounter++}`,
          ingredient_id: existing!.id,
          selected: { ...existing!, source: "ingredient", is_untracked: !!existing!.is_untracked },
          display_name: "",
          amount: default_amount,
          unit: existing!.default_unit || default_unit,
          scale_type: "exact",
        },
      ]);
      flashFeedback(`Added "${name}" to recipe!`);
    }
  }

  // ── Instruction helpers ──
  function addStep() {
    setInstructions((prev) => [...prev, { key: `step-${tmpCounter++}`, text: "" }]);
  }
  function updateStep(idx: number, value: string) {
    setInstructions((prev) => prev.map((s, i) => (i === idx ? { ...s, text: value } : s)));
  }
  function removeStep(idx: number) {
    setInstructions((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveStep(from: number, to: number) {
    if (to < 0 || to >= instructions.length) return;
    setInstructions((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  // ── Finish step helpers (Phase 8b) ──
  function addFinishStep() {
    setFinishSteps((prev) => [
      ...prev,
      {
        key: `tmp-${tmpCounter++}`,
        finish_type: "Garnish",
        label: "",
        instruction: "",
        quantity: "",
        unit: "",
        scale_type: "per_cocktail",
        required: true,
      },
    ]);
  }
  function updateFinishStep(idx: number, patch: Partial<FinishStepRow>) {
    setFinishSteps((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeFinishStep(idx: number) {
    setFinishSteps((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveFinishStep(from: number, to: number) {
    if (to < 0 || to >= finishSteps.length) return;
    setFinishSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }
  function duplicateFinishStep(idx: number) {
    setFinishSteps((prev) => {
      const next = [...prev];
      const src = next[idx];
      next.splice(idx + 1, 0, { ...src, key: `tmp-${tmpCounter++}` });
      return next;
    });
  }

  if (loading) {
    return (
      <main className="recipe-editor" style={styles.shell}>
        <p style={{ color: "var(--muted)" }}>Loading editor…</p>
      </main>
    );
  }

  if (readOnly) {
    return (
      <main className="recipe-editor" style={styles.shell}>
        <p style={styles.error}>
          This version is <strong>{versionStatus}</strong> and cannot be edited.
          Approved and Retired versions are immutable. Use "Create Draft From
          Approved" on the Recipes page to make changes.
        </p>
      </main>
    );
  }

  const stickyActions = (
    <div className="recipe-editor-actions" style={styles.stickyActions}>
      {feedback && <span style={styles.feedback}>{feedback}</span>}
      {error && <span style={styles.errorInline}>{error}</span>}
      <details className="recipe-editor-more">
        <summary>More actions</summary>
        <div className="recipe-editor-more-panel">
          <button
            type="button"
            onClick={onDiscard}
            disabled={discarding || saving || publishing}
            style={styles.dangerBtn}
          >
            {discarding ? "…" : "Discard draft"}
          </button>
        </div>
      </details>
      {/* Non-owners go through the Needs Review queue; owners skip it
          and publish directly with one tap. The "Submit for review" path
          stays alive for bar_lead / bar_manager workflows. */}
      {!isOwner && (
        <button
          type="button"
          onClick={onSubmitForReview}
          disabled={submittingReview || saving}
          style={styles.secondaryBtn}
        >
          {submittingReview ? "…" : "Submit for review"}
        </button>
      )}
      <button
        type="button"
        onClick={onSave}
        className="recipe-editor-save"
        disabled={saving || submittingReview || publishing}
        style={isOwner ? styles.secondaryBtn : styles.primaryBtn}
      >
        {saving ? "Saving…" : "Save draft"}
      </button>
      {isOwner && (
        <button
          type="button"
          onClick={onSubmitAndPublish}
          className="recipe-editor-publish"
          disabled={publishing || saving || submittingReview}
          style={styles.primaryBtn}
          title="Save current edits and publish as the live version (skips the Needs Review queue)."
        >
          {publishing ? "Publishing…" : "Submit & publish"}
        </button>
      )}
    </div>
  );

  return (
    <main className="recipe-editor" style={styles.shell}>
      <header style={styles.headerBar}>
        <div>
          <p style={styles.eyebrow}>
            Editing v{versionNumber} · {versionStatus}
          </p>
          <h1 style={styles.title}>{name || "(unnamed cocktail)"}</h1>
        </div>
        <button
          type="button"
          onClick={async () => {
            if (dirtyRef.current) {
              const ok = await confirm({
                title: "Discard unsaved edits?",
                message: "You have changes that haven't been saved. Leaving will lose them.",
                confirmLabel: "Discard & leave",
                cancelLabel: "Stay",
                danger: true,
              });
              if (!ok) return;
            }
            navigate(drinkSlug ? `/admin/recipes/${drinkSlug}` : "/admin/recipes");
          }}
          style={styles.backBtn}
        >
          ← Back
        </button>
      </header>

      {stickyActions}

      <Section title="A. Basic info">
        <FieldRow>
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} style={styles.input} />
          </Field>
          <Field label="Category">
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={styles.input}
              placeholder="Whiskey · Clarified · Rum · Gin …"
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Tags (comma-separated)">
            <input
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              style={styles.input}
              placeholder="Old Fashioned, Smoked, House Standard"
            />
          </Field>
          <Field label="Search aliases (comma-separated)">
            <input
              value={aliasesRaw}
              onChange={(e) => setAliasesRaw(e.target.value)}
              style={styles.input}
              placeholder="of, old fash, sentinel of"
            />
            <p style={styles.hint}>
              Bartenders search shorthand during service. Add what they'd
              actually type.
            </p>
          </Field>
        </FieldRow>
      </Section>

      <Section title="B. Service settings">
        <div style={styles.menuRow}>
          <div>
            <div style={styles.menuRowTitle}>Bartender menu</div>
            <p style={{ ...styles.hint, margin: 0 }}>
              Saves immediately, separate from the draft.
              {!hasApprovedVersion && serviceStatus !== "Active" &&
                " Needs an approved version before it can go on."}
            </p>
          </div>
          <MenuToggle
            isOn={serviceStatus === "Active"}
            canToggle={serviceStatus === "Active" || hasApprovedVersion}
            busy={statusBusy}
            onToggle={() => void setLiveServiceStatus(serviceStatus === "Active" ? "Hidden" : "Active")}
          />
        </div>
        <FieldRow>
          <Field label="Recipe status">
            <select
              value={recipeStatus}
              onChange={(e) => setRecipeStatus(e.target.value as any)}
              style={styles.input}
            >
              {RECIPE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <p style={styles.hint}>
              Approved + Retired are managed via the Version Panel actions
              (Phase 7).
            </p>
          </Field>
          <Field label="Service status">
            <select
              value={serviceStatus}
              onChange={(e) => void setLiveServiceStatus(e.target.value)}
              style={styles.input}
              disabled={statusBusy}
            >
              {SERVICE_STATUSES.map((s) => (
                <option
                  key={s}
                  value={s}
                  // The current value stays selectable so a legacy status still displays.
                  disabled={!hasApprovedVersion && REQUIRES_APPROVED.has(s) && s !== serviceStatus}
                >
                  {s}
                </option>
              ))}
            </select>
            <p style={styles.hint}>Saves immediately, like the menu switch.</p>
          </Field>
        </FieldRow>
        <Field label="">
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={featuredToday}
              onChange={(e) => setFeaturedToday(e.target.checked)}
            />
            <span>Featured Today (sets 24h flag, surfaces in Station's Today filter)</span>
          </label>
        </Field>
      </Section>

      <Section title="C. Drink build">
        <div className="recipe-editor-build-fields" style={styles.buildGrid}>
          <Field label="Vessel">
            <select
              value={vessel}
              onChange={(e) => setVessel(e.target.value)}
              style={styles.input}
              disabled={readOnly}
            >
              <option value="">— not specified —</option>
              <option value="Mixing Glass">Mixing Glass</option>
              <option value="Shaker">Shaker</option>
            </select>
          </Field>
          <Field label="Method">
            <StandardSelect
              kind="method"
              value={method}
              onChange={setMethod}
              standards={standards}
              disabled={readOnly}
            />
          </Field>
          <Field label="Glass">
            <StandardSelect
              kind="glass"
              value={glass}
              onChange={setGlass}
              standards={standards}
              disabled={readOnly}
            />
          </Field>
          <Field label="Ice">
            <StandardSelect
              kind="ice"
              value={ice}
              onChange={setIce}
              standards={standards}
              disabled={readOnly}
            />
          </Field>
          <Field label="Garnish">
            <input
              value={garnish}
              onChange={(e) => setGarnish(e.target.value)}
              style={styles.input}
              disabled={readOnly}
            />
          </Field>
          <Field label="Smoked">
            <label style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6 }}>
              <input
                type="checkbox"
                checked={smoked}
                onChange={(e) => setSmoked(e.target.checked)}
                disabled={readOnly}
              />
              <span>Smoked cocktail</span>
            </label>
          </Field>
          <Field label="Frozen">
            <label style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6 }}>
              <input
                type="checkbox"
                checked={frozen}
                onChange={(e) => setFrozen(e.target.checked)}
                disabled={readOnly}
              />
              <span>Frozen cocktail</span>
            </label>
          </Field>
        </div>
        <Field label="Warning (rendered near the top of bartender screen)">
          <textarea
            value={warning}
            onChange={(e) => setWarning(e.target.value)}
            style={styles.textarea}
            rows={2}
            placeholder='e.g. "86 — pineapple batch unavailable" or "Recipe changed Apr 30: syrup reduced"'
          />
        </Field>
      </Section>

      <Section title="D. Ingredients">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <p style={{ ...styles.muted, margin: 0 }}>
            {rows.length === 0 ? "No ingredients yet. Add one below." : `${rows.length} ingredient line item(s)`}
          </p>
          <button
            type="button"
            onClick={() => {
              setQuickIngName("");
              setQuickIngCategory("spirit");
              setQuickIngUnit("oz");
              setQuickIngUntracked(false);
              setQuickIngError(null);
              setShowQuickIngModal({ targetRowIdx: null });
            }}
            style={styles.secondaryBtnSmall}
            title="Create a new bottle or ingredient not yet in the master database"
          >
            + Quick Add Ingredient / Bottle
          </button>
        </div>

        {/* Quick Add Untracked Bar Staples */}
        <div style={{ marginBottom: 12, padding: "10px 12px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "var(--gold)", letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
            Quick Add Basic / Untracked Staples:
          </span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { name: "Water", cat: "other", unit: "oz", amt: "1" },
              { name: "Filtered Water", cat: "other", unit: "oz", amt: "1" },
              { name: "Hot Water", cat: "other", unit: "oz", amt: "1" },
              { name: "Club Soda", cat: "other", unit: "oz", amt: "1" },
              { name: "Ice", cat: "other", unit: "each", amt: "1" },
              { name: "Saline Solution (20%)", cat: "bitters", unit: "drops", amt: "4" },
              { name: "Salt", cat: "other", unit: "pinch", amt: "1" },
              { name: "Sugar Cube", cat: "syrup", unit: "each", amt: "1" },
            ].map((ess) => (
              <button
                key={ess.name}
                type="button"
                onClick={() => handleQuickAddBasic(ess.name, ess.cat, ess.unit, ess.amt)}
                style={styles.essentialChip}
                title={`Add ${ess.name} (untracked staple)`}
              >
                + {ess.name}
              </button>
            ))}
          </div>
        </div>

        {rows.map((row, idx) => {
          const master = row.selected;
          return (
            <div key={row.key} className="recipe-editor-ingredient-card" style={styles.ingredientRow}>
              <h3 className="recipe-editor-card-title">Ingredient {idx + 1}</h3>
              <div className="recipe-editor-ingredient-fields" style={styles.ingredientGrid}>
                <div style={{ display: "grid", gap: 6, gridColumn: "1 / -1", minWidth: 0 }}>
                  <RecipeIngredientPicker
                    label={`Search ingredient ${idx + 1}`}
                    selected={row.selected}
                    onSelect={(next) => updateRow(idx, {
                      ingredient_id: next.id,
                      selected: next,
                      display_name: "",
                      unit: !master || row.unit === master.default_unit ? next.default_unit : row.unit,
                    })}
                    onCreate={(name) => {
                      // A typed-in name the search couldn't find is usually a garnish
                      // (peels, sprigs, picks) — default to that; the modal can change it.
                      setQuickIngName(name);
                      setQuickIngCategory(name ? "garnish" : "spirit");
                      setQuickIngUnit(name ? "each" : "oz");
                      setQuickIngUntracked(false);
                      setQuickIngError(null);
                      setShowQuickIngModal({ targetRowIdx: idx });
                    }}
                  />
                </div>
                {master && <p className="recipe-editor-selected-name">{master.name} ({master.category}){master.is_untracked ? " · Basic/Untracked" : ""}{!master.available ? " (unavailable)" : ""}</p>}
                <Field label="Amount">
                  <input
                    value={row.amount}
                    onChange={(e) => updateRow(idx, { amount: e.target.value })}
                    style={styles.input}
                    placeholder="2"
                    inputMode="decimal"
                  />
                </Field>
                <Field label="Unit">
                  <input
                    value={row.unit}
                    onChange={(e) => updateRow(idx, { unit: e.target.value })}
                    style={styles.input}
                    placeholder="oz"
                  />
                </Field>
                <Field label="Scaling" style={{ display: "grid", gap: 6, gridColumn: "1 / -1", minWidth: 0 }}>
                  <select
                    value={row.scale_type}
                    onChange={(e) =>
                      updateRow(idx, { scale_type: e.target.value as ScaleType })
                    }
                    style={styles.input}
                  >
                    {SCALE_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Display name (optional)" style={{ display: "grid", gap: 6, gridColumn: "1 / -1", minWidth: 0 }}>
                  <input
                    value={row.display_name}
                    onChange={(e) =>
                      updateRow(idx, { display_name: e.target.value })
                    }
                    style={styles.input}
                    placeholder={`Display as (default: ${master?.name ?? row.ingredient_id})`}
                  />
                </Field>
              </div>
              <div className="recipe-editor-row-actions" style={styles.ingredientActions}>
                <button
                  type="button"
                  onClick={() => moveRow(idx, idx - 1)}
                  disabled={idx === 0}
                  style={styles.iconBtn}
                  aria-label="Move up"
                >
                  <span aria-hidden="true">▲</span><span className="recipe-editor-action-label">Move up</span>
                </button>
                <button
                  type="button"
                  onClick={() => moveRow(idx, idx + 1)}
                  disabled={idx === rows.length - 1}
                  style={styles.iconBtn}
                  aria-label="Move down"
                >
                  <span aria-hidden="true">▼</span><span className="recipe-editor-action-label">Move down</span>
                </button>
                <button
                  type="button"
                  onClick={() => duplicateRow(idx)}
                  style={styles.iconBtn}
                  aria-label="Duplicate"
                  title="Duplicate"
                >
                  <span aria-hidden="true">⧉</span><span className="recipe-editor-action-label">Duplicate</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  style={styles.iconBtnDanger}
                  aria-label="Remove"
                >
                  <span aria-hidden="true">✕</span><span className="recipe-editor-action-label">Remove</span>
                </button>
              </div>
            </div>
          );
        })}
        <button type="button" onClick={addIngredientRow} style={styles.addBtn}>
          + Add ingredient
        </button>
      </Section>

      <Section title="E. Build instructions">
        {instructions.length === 0 && (
          <p style={styles.muted}>No steps yet. Add the first one below.</p>
        )}
        {instructions.map((step, idx) => (
          <div key={step.key} className="recipe-editor-instruction-card" style={styles.stepRow}>
            <label htmlFor={step.key} className="recipe-editor-step-number" style={styles.stepNum}><span className="recipe-editor-action-label">Step </span>{idx + 1}</label>
            <textarea
              id={step.key}
              aria-label={`Build step ${idx + 1}`}
              value={step.text}
              ref={(element) => {
                if (element) {
                  element.style.height = "auto";
                  element.style.height = `${element.scrollHeight}px`;
                }
              }}
              onChange={(e) => updateStep(idx, e.target.value)}
              style={styles.textarea}
              rows={2}
            />
            <div className="recipe-editor-row-actions" style={styles.stepActions}>
              <button
                type="button"
                aria-label="Move up"
                onClick={() => moveStep(idx, idx - 1)}
                disabled={idx === 0}
                style={styles.iconBtn}
              >
                <span aria-hidden="true">▲</span><span className="recipe-editor-action-label">Move up</span>
              </button>
              <button
                type="button"
                aria-label="Move down"
                onClick={() => moveStep(idx, idx + 1)}
                disabled={idx === instructions.length - 1}
                style={styles.iconBtn}
              >
                <span aria-hidden="true">▼</span><span className="recipe-editor-action-label">Move down</span>
              </button>
              <button
                type="button"
                aria-label="Remove"
                onClick={() => removeStep(idx)}
                style={styles.iconBtnDanger}
              >
                <span aria-hidden="true">✕</span><span className="recipe-editor-action-label">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addStep} style={styles.addBtn}>
          + Add step
        </button>
      </Section>

      <Section title="F. Finish / Presentation">
        <p style={styles.muted}>
          What happens AFTER the drink is built — garnish, smoke, cream
          top, foam, rim, mist, float, presentation, serveware. Each step
          has its own scale rule, so &quot;smoke chamber&quot; stays singular
          while &quot;orange peels&quot; multiply with cocktail count.
        </p>
        {finishSteps.length === 0 && (
          <p style={styles.muted}>No finish steps yet. Add the first one below.</p>
        )}
        {finishSteps.map((row, idx) => (
          <div key={row.key} className="recipe-editor-finish-card" style={styles.finishRow}>
            <h3 className="recipe-editor-card-title">Finish step {idx + 1}</h3>
            <div className="recipe-editor-finish-fields" style={styles.finishGrid}>
              <Field label="Type">
                <select
                  value={row.finish_type}
                  onChange={(e) => updateFinishStep(idx, { finish_type: e.target.value as FinishType })}
                  style={styles.input}
                >
                  {FINISH_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Label (short)">
                <input
                  value={row.label}
                  onChange={(e) => updateFinishStep(idx, { label: e.target.value })}
                  placeholder='e.g. "Express orange peel"'
                  style={styles.input}
                />
              </Field>
              <Field label="Scale">
                <select
                  value={row.scale_type}
                  onChange={(e) => updateFinishStep(idx, { scale_type: e.target.value as FinishScaleType })}
                  style={styles.input}
                >
                  {FINISH_SCALE_TYPES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Required">
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={row.required}
                    onChange={(e) => updateFinishStep(idx, { required: e.target.checked })}
                  />
                  <span>{row.required ? "Required" : "Optional"}</span>
                </label>
              </Field>
            </div>
            <Field label="Instruction (long, shown on Station)">
              <textarea
                value={row.instruction}
                onChange={(e) => updateFinishStep(idx, { instruction: e.target.value })}
                placeholder='e.g. "Express orange peel over drink and place on cube."'
                style={styles.textarea}
                rows={2}
              />
            </Field>
            <FieldRow>
              <Field label="Quantity (optional, e.g. 0.5 for cream top)">
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  value={row.quantity}
                  onChange={(e) => updateFinishStep(idx, { quantity: e.target.value })}
                  style={styles.input}
                />
              </Field>
              <Field label="Unit (optional)">
                <input
                  value={row.unit}
                  onChange={(e) => updateFinishStep(idx, { unit: e.target.value })}
                  placeholder='oz · drops · each'
                  style={styles.input}
                />
              </Field>
            </FieldRow>
            <div className="recipe-editor-row-actions" style={styles.finishActions}>
              <button
                type="button"
                onClick={() => moveFinishStep(idx, idx - 1)}
                disabled={idx === 0}
                style={styles.iconBtn}
                title="Move up"
              >
                <span aria-hidden="true">▲</span><span className="recipe-editor-action-label">Move up</span>
              </button>
              <button
                type="button"
                onClick={() => moveFinishStep(idx, idx + 1)}
                disabled={idx === finishSteps.length - 1}
                style={styles.iconBtn}
                title="Move down"
              >
                <span aria-hidden="true">▼</span><span className="recipe-editor-action-label">Move down</span>
              </button>
              <button
                type="button"
                onClick={() => duplicateFinishStep(idx)}
                style={styles.iconBtn}
                title="Duplicate"
              >
                <span aria-hidden="true">⧉</span><span className="recipe-editor-action-label">Duplicate</span>
              </button>
              <button
                type="button"
                onClick={() => removeFinishStep(idx)}
                style={styles.iconBtnDanger}
                title="Remove"
              >
                <span aria-hidden="true">✕</span><span className="recipe-editor-action-label">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addFinishStep} style={styles.addBtn}>
          + Add finish step
        </button>
      </Section>

      <Section title="G. Prep recipe links">
        <p style={styles.muted}>
          Phase 9 ships the prep recipes section + linker. For now, drinks
          can reference a prep ingredient (Clarified House Batch, Demerara
          Syrup, etc.) via their ingredient row above.
        </p>
      </Section>

      <Section title="H. Notes">
        <Field label="Staff notes — visible to bartenders">
          <textarea
            value={staffNotes}
            onChange={(e) => setStaffNotes(e.target.value)}
            style={styles.textarea}
            rows={3}
          />
        </Field>
        <Field label="Internal admin notes — admin-only, never shown to bartenders">
          <textarea
            value={internalAdminNotes}
            onChange={(e) => setInternalAdminNotes(e.target.value)}
            style={styles.textarea}
            rows={3}
          />
        </Field>
        <Field label="Testing notes — admin-only">
          <textarea
            value={testingNotes}
            onChange={(e) => setTestingNotes(e.target.value)}
            style={styles.textarea}
            rows={2}
          />
        </Field>
        <Field label="Change summary — required at approval (Phase 7)">
          <textarea
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            style={styles.textarea}
            rows={2}
            placeholder="One sentence on what changed and why."
          />
        </Field>
      </Section>

      <div className="recipe-editor-bottom-actions" style={{ marginBottom: 80 }}>{stickyActions}</div>
      {confirmDialog}

      {/* Quick Add Ingredient / Bottle Modal */}
      {showQuickIngModal && (
        <div style={styles.modalOverlay} onClick={() => setShowQuickIngModal(null)}>
          <div style={styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Add New Ingredient / Bottle</h3>
                <p style={styles.modalMeta}>
                  Creates a master ingredient record available to all recipes across Station
                </p>
              </div>
              <button
                type="button"
                style={styles.closeBtn}
                onClick={() => setShowQuickIngModal(null)}
              >
                ✕
              </button>
            </div>

            {quickIngError && (
              <div style={{ ...styles.error, marginBottom: 12 }} role="alert">
                {quickIngError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={styles.fieldLabel}>Ingredient / Bottle Name *</label>
                <input
                  type="text"
                  value={quickIngName}
                  onChange={(e) => setQuickIngName(e.target.value)}
                  placeholder="e.g. Water, Fords Gin, Demerara Syrup"
                  style={styles.input}
                  autoFocus
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                <div>
                  <label style={styles.fieldLabel}>Category</label>
                  <select
                    value={quickIngCategory}
                    onChange={(e) => setQuickIngCategory(e.target.value)}
                    style={styles.input}
                  >
                    {INGREDIENT_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1).replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.fieldLabel}>Default Unit</label>
                  <select
                    value={quickIngUnit}
                    onChange={(e) => setQuickIngUnit(e.target.value)}
                    style={styles.input}
                  >
                    {INGREDIENT_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={quickIngUntracked}
                    onChange={(e) => setQuickIngUntracked(e.target.checked)}
                  />
                  <span>Basic / Untracked ingredient (e.g. water, ice, tap items — exempt from 86 stockouts)</span>
                </label>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={() => setShowQuickIngModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={styles.primaryBtn}
                  disabled={quickIngSaving}
                  onClick={handleQuickCreateIngredient}
                >
                  {quickIngSaving ? "Creating…" : "Save & Add to Recipe"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

// ─────────────────────────── Tiny components ───────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="recipe-editor-section" style={styles.section}>
      <h2 className="recipe-editor-section-title" style={styles.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

// Field is imported from ../../components/admin/Field (shared a11y-correct version).
function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="recipe-editor-field-row" style={styles.fieldRow}>{children}</div>;
}

interface StandardSelectProps {
  kind: RecipeStandardKind;
  value: string;
  onChange: (next: string) => void;
  standards: { glass: RecipeStandardRow[]; ice: RecipeStandardRow[]; method: RecipeStandardRow[] } | null;
  disabled?: boolean;
}

/**
 * Dropdown sourced from the admin-managed recipe_standards table. Falls back
 * to a free-text input if standards haven't loaded or the current value isn't
 * in the list (so legacy or seasonal one-offs don't get clobbered).
 */
function StandardSelect({ kind, value, onChange, standards, disabled }: StandardSelectProps) {
  const list = [...(standards?.[kind] ?? [])].sort((a, b) => a.value.localeCompare(b.value));
  const inList = list.some((row) => row.value === value);
  const showCustom = !!value && !inList;

  if (list.length === 0) {
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.input}
        disabled={disabled}
        placeholder={`Add ${kind} options in Settings`}
      />
    );
  }

  return (
    <select
      value={showCustom ? "__custom__" : value}
      onChange={(e) => {
        const next = e.target.value;
        if (next === "__custom__") return; // ignore — meta option
        onChange(next);
      }}
      style={styles.input}
      disabled={disabled}
    >
      <option value="">— select {kind} —</option>
      {list.map((row) => (
        <option key={row.id} value={row.value}>{row.value}</option>
      ))}
      {showCustom && (
        <option value="__custom__">{value} (custom — edit in Settings to standardize)</option>
      )}
    </select>
  );
}

// ─────────────────────────── Styles ───────────────────────────

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 920, paddingBottom: 100 },
  headerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    margin: "4px 0 0",
    fontSize: 28,
    letterSpacing: -0.4,
  },
  backBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    color: "var(--muted)",
    borderRadius: 10,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  stickyActions: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    padding: "10px 0",
    background: "var(--bg)",
    borderBottom: "1px solid var(--border)",
    marginBottom: 16,
  },
  feedback: {
    color: "#86efac",
    fontSize: 12,
    fontWeight: 700,
    background: "rgba(39,174,96,0.16)",
    border: "1px solid rgba(39,174,96,0.4)",
    padding: "6px 10px",
    borderRadius: 10,
  },
  errorInline: {
    color: "#FF8A7A",
    fontSize: 12,
    fontWeight: 700,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    padding: "6px 10px",
    borderRadius: 10,
    flexBasis: "100%",
  },
  primaryBtn: {
    marginLeft: "auto",
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "var(--surface)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  dangerBtn: {
    background: "transparent",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  section: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
  },
  sectionTitle: {
    margin: "0 0 12px",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  field: { display: "grid", gap: 6, marginBottom: 12 },
  // Mobile responsiveness (WCAG 1.4.10 reflow): two-up at ≥600px CSS pixels,
  // single column on phones via `auto-fit + minmax`. No JS breakpoint needed.
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
    gap: 12,
  },
  buildGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
    gap: 12,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  hint: { margin: "4px 0 0", fontSize: 11, color: "var(--muted)" },
  menuRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    marginBottom: 12,
    border: "1px solid var(--border)",
    borderRadius: 10,
  },
  menuRowTitle: { fontSize: 13, fontWeight: 700 },
  input: {
    height: 36,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 10px",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
  },
  textarea: {
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: 10,
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "var(--text)",
    fontWeight: 600,
  },
  ingredientRow: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 10,
    marginBottom: 8,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 8,
    alignItems: "start",
  },
  ingredientGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: 6,
  },
  ingredientActions: { display: "flex", gap: 4 },
  // WCAG 2.5.5 — 40×40 minimum (gap-based hit-slop covers the rest).
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnDanger: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: "1px solid rgba(224,80,64,0.4)",
    background: "rgba(224,80,64,0.1)",
    color: "#FF8A7A",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  addBtn: {
    background: "transparent",
    color: "var(--gold)",
    border: "1px dashed var(--gold)",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    width: "100%",
  },
  stepRow: {
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1fr) auto",
    gap: 8,
    alignItems: "start",
    marginBottom: 8,
  },
  stepNum: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: "var(--gold)",
    color: "#0D0B08",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 12,
    marginTop: 4,
  },
  stepActions: { display: "flex", gap: 4 },
  finishRow: {
    display: "grid",
    gap: 8,
    padding: 12,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    marginBottom: 10,
  },
  finishGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 8,
  },
  finishActions: {
    display: "flex",
    gap: 6,
    justifyContent: "flex-end",
  },
  secondaryBtnSmall: {
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modalBody: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    width: 480,
    maxWidth: "95vw",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: 24,
    boxShadow: "0 16px 40px rgba(0, 0, 0, 0.5)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    color: "var(--gold)",
    margin: "0 0 4px 0",
  },
  modalMeta: {
    fontSize: 12,
    color: "var(--muted)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    color: "var(--muted)",
    cursor: "pointer",
    padding: "4px 8px",
  },
  muted: { color: "var(--muted)", fontSize: 13, padding: "6px 0" },
  essentialChip: {
    background: "rgba(255, 255, 255, 0.06)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
  },
  error: {
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    padding: 16,
    borderRadius: 12,
    fontSize: 13,
  },
};
